import { useCallback, useEffect, useRef, useState } from "react";
import MarketScanner from "./MarketScanner";
import HowToPlay from "./HowToPlay";
import { useAutoRun, AutoRunBar } from "./AutoRun";
import { useServerBurst, ServerBurstPanel } from "./ServerBurst";
import { StageFX, CameraFlashes } from "./CasinoFX";
import { sfxCountdownBeep, sfxEngineRev, sfxEngineRoar, sfxScreech, sfxFinishHorn } from "@/lib/arcadeSound";
import "./arcade-anim.css";

const CAR_IMG = { red: "/arcade/car_red.png", blue: "/arcade/car_blue.png" };

/* 🔥 nitro flame behind a racing car */
const Nitro = ({ on }) => on ? (
  <span className="absolute top-1/2 -translate-y-1/2 -left-7 w-8 h-3 rounded-full cr-nitro pointer-events-none"
    style={{ background: "linear-gradient(90deg, transparent, #ffb84d 40%, #fff6d6 85%)", filter: "blur(1.5px)" }} />
) : null;

/* 🚥 F1 start gantry */
const Lights = ({ step }) => (
  <div className="flex gap-1.5 items-center rounded-md bg-black/80 border border-white/15 px-2.5 py-1.5">
    {[1, 2, 3].map((i) => (
      <span key={i} className="w-3.5 h-3.5 rounded-full transition-colors"
        style={{ background: step >= i && step < 4 ? "#F23645" : "#161b28", boxShadow: step >= i && step < 4 ? "0 0 10px #F23645" : "none" }} />
    ))}
    <span className="w-3.5 h-3.5 rounded-full transition-colors"
      style={{ background: step >= 4 ? "#089981" : "#161b28", boxShadow: step >= 4 ? "0 0 12px #089981" : "none" }} />
  </div>
);

/* 🏎 Car Race — Red vs Blue over ONE real Deriv contract. Two modes (same
   toggle as Penalty): DIGITS (Even/Odd, 5 ticks) or RISE/FALL (1 tick).
   The animation always lands on the real settled outcome. */
const CarRace = ({ runContract, busy, baseStake = 0.35, logExternal }) => {
  const [picked, setPicked] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | lights | racing | done
  const [lightStep, setLightStep] = useState(0);
  const [res, setRes] = useState(null);
  const [tally, setTally] = useState({ w: 0, l: 0, net: 0 });
  const [mkt, setMkt] = useState(null);
  const [carMode, setCarMode] = useState(() => { try { return localStorage.getItem("vts_car_mode") === "risefall" ? "risefall" : "digits"; } catch { return "digits"; } });
  const modeRef = useRef(carMode);
  useEffect(() => { modeRef.current = carMode; try { localStorage.setItem("vts_car_mode", carMode); } catch { /* noop */ } }, [carMode]);
  const symRef = useRef("1HZ100V");
  const mktRef = useRef(null);
  const onScan = useCallback((row) => { symRef.current = row.sym; mktRef.current = row; setMkt(row); }, []);
  const auto = useAutoRun("carrace");
  // 🎬 while a server season runs and you're WATCHING, every server race
  // replays live on the track and lands in the session history
  const replayServerRace = useCallback((fresh, stB) => {
    fresh.forEach((r) => logExternal?.({
      label: "Car Race", won: r.won, profit: r.profit, stake: r.stake,
      digit: r.digit, side: stB.side, sym: stB.symbol,
    }));
    const r = fresh[fresh.length - 1];
    const fw = fresh.filter((x) => x.won).length;
    const fl = fresh.length - fw;
    const fnet = fresh.reduce((a, x) => a + x.profit, 0);
    const car = (stB.side === "EVEN" || stB.side === "FALL") ? "red" : "blue";
    setPicked(car); setRes(null); setPhase("racing"); sfxEngineRoar();
    setTimeout(() => {
      sfxScreech();
      if (r.won) sfxFinishHorn();
      const winner = r.won ? car : (car === "red" ? "blue" : "red");
      setRes({ won: r.won, profit: r.profit, digit: r.digit, car, winner,
        mode: stB.kind === "risefall" ? "risefall" : "digits" });
      setPhase("done");
      setTally((t) => ({ w: t.w + fw, l: t.l + fl, net: Math.round((t.net + fnet) * 100) / 100 }));
    }, 1300);
  }, [logExternal]);
  const srv = useServerBurst("carrace", replayServerRace);
  const racing = phase === "lights" || phase === "racing";
  const rf = carMode === "risefall";

  // 🔴 red = EVEN (digits) / FALL (rise-fall) · 🔵 blue = ODD / RISE
  const sideFor = (car, mode = modeRef.current) =>
    mode === "risefall" ? (car === "blue" ? "RISE" : "FALL") : (car === "red" ? "EVEN" : "ODD");
  const carForSignal = (side) => (side === "EVEN" || side === "FALL") ? "red" : "blue";

  const race = async (car) => {
    if (racing || busy) return null;
    const c = car ?? picked;
    if (!c) return null;
    const mode = modeRef.current;
    setPicked(c); setRes(null); setPhase("lights");
    for (let i = 1; i <= 3; i += 1) {
      setLightStep(i); sfxCountdownBeep(false);
      await new Promise((r) => setTimeout(r, 420));
    }
    setLightStep(4); sfxCountdownBeep(true); sfxEngineRev();
    setPhase("racing"); sfxEngineRoar();
    const r = await runContract(mode === "risefall"
      ? { contractType: c === "blue" ? "CALL" : "PUT", duration: 1, side: sideFor(c, mode), label: "Car Race", symbol: symRef.current }
      : { contractType: c === "red" ? "DIGITEVEN" : "DIGITODD", duration: 5, side: `${c.toUpperCase()} (${sideFor(c, mode)})`, label: "Car Race", symbol: symRef.current });
    if (!r) { setPhase("idle"); setLightStep(0); return null; }
    sfxScreech();
    if (r.won) sfxFinishHorn();
    const winner = r.won ? c : (c === "red" ? "blue" : "red");
    setRes({ ...r, car: c, winner, mode });
    setPhase("done"); setLightStep(0);
    setTally((t) => ({ w: t.w + (r.won ? 1 : 0), l: t.l + (r.won ? 0 : 1), net: Math.round((t.net + r.profit) * 100) / 100 }));
    return r;
  };

  const fire = (car) => {
    if (!car) return;
    if (auto.runs >= 50) {
      srv.start({ kind: rf ? "risefall" : "evenodd", side: sideFor(car), n: auto.runs,
        stake: baseStake, symbol: symRef.current, duration: rf ? 1 : 5 });
      return;
    }
    auto.burst(race, car);
  };

  const carPos = (car) => {
    if (phase === "done" && res) return res.winner === car ? "73%" : "48%";
    if (phase === "racing") return "32%";
    return "3%";
  };
  const locked = busy || racing || auto.active || srv.running;

  return (
    <div data-testid="carrace-game" className="space-y-4">
      <HowToPlay id="carrace"
        steps={[
          "Pick your mode: 🏁 DIGITS (Even/Odd, 5 ticks) or 📈 RISE/FALL (1 tick) — same toggle as Penalty.",
          "Pick your car: 🔴 RED rides EVEN digits (or FALL), 🔵 BLUE rides ODD digits (or RISE).",
          "Press RACE — the start lights count down and ONE real Deriv contract fires on the scanner-locked market.",
          "Your car crosses the line first only when the REAL contract wins — the animation always shows the real settled outcome (~×1.95 / ~×1.9 payout, or the stake is lost).",
        ]}
        win="digits: the last digit matches your car (even = Red, odd = Blue) · rise/fall: the tick closes the way your car calls it (Blue = higher, Red = lower)."
        payout="live Deriv odds — ~×1.95 on Even/Odd, ~×1.9 on Rise/Fall, identical to the other games."
        tip="it's ~50/50 every race no matter what won before — size stakes you can afford and bank good sessions." />

      {/* 🏁 the track */}
      <div className="relative rounded-xl border-2 border-[#F7B731]/50 overflow-hidden h-60"
        style={{ backgroundImage: "url(/arcade/carrace_bg.jpeg)", backgroundSize: "cover", backgroundPosition: "center 40%", boxShadow: "inset 0 0 70px rgba(0,0,0,.65)" }}>
        <div className="absolute inset-0 bg-black/40" />
        {/* speed-line wash while racing */}
        {phase === "racing" && (
          <div className="absolute inset-0 cr-speedlines pointer-events-none" style={{
            backgroundImage: "repeating-linear-gradient(90deg, transparent 0 46px, rgba(255,255,255,.14) 46px 52px)",
          }} />
        )}
        <StageFX outcome={res && phase === "done" ? (res.won ? "win" : "loss") : null} />
        {phase === "done" && res?.won && <CameraFlashes />}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10"><Lights step={lightStep} /></div>

        {/* finish line + waving checkered flag */}
        <div className="absolute right-[7%] top-12 bottom-3 w-2 opacity-85"
          style={{ background: "repeating-linear-gradient(0deg,#fff 0 6px,#111 6px 12px)" }} />
        <div className="absolute right-[4.5%] top-8 origin-bottom-left" style={{ animation: phase === "done" ? "cr-flag .5s ease-in-out 4" : "none" }}>
          <div className="w-7 h-5" style={{
            backgroundImage: "conic-gradient(#fff 0 25%, #111 0 50%, #fff 0 75%, #111 0)",
            backgroundSize: "8px 8px", border: "1px solid rgba(255,255,255,.4)",
          }} />
          <div className="w-0.5 h-10 bg-white/70 ml-0" />
        </div>

        {/* lanes */}
        {["red", "blue"].map((car, i) => {
          const isWinner = phase === "done" && res?.winner === car;
          const isRunning = phase === "racing";
          return (
            <div key={car} className="absolute left-0 right-0" style={{ top: i === 0 ? "38%" : "64%" }}>
              <div className={`absolute ${isRunning ? (car === "red" ? "cr-duel-a" : "cr-duel-b") : ""} ${isWinner ? "cr-surge" : ""}`}
                style={{
                  left: carPos(car),
                  transition: "left " + (phase === "done" ? "900ms cubic-bezier(.2,.9,.3,1)" : phase === "racing" ? "4500ms linear" : "400ms ease"),
                  filter: isRunning ? "blur(0.5px)" : "none",
                }}>
                <div className="relative">
                  <Nitro on={isRunning || isWinner} />
                  {isRunning && <span className="absolute -left-14 top-1/2 w-14 h-1.5 rounded bg-white/30 blur-[2px]" />}
                  <img src={CAR_IMG[car]} alt={`${car} race car`} className="h-11 w-auto max-w-none"
                    style={{
                      filter: `drop-shadow(0 5px 8px rgba(0,0,0,.7))${picked === car || res?.winner === car ? ` drop-shadow(0 0 10px ${car === "red" ? "#F23645" : "#2962FF"})` : ""}`,
                      animation: isRunning ? "cr-vibe .12s linear infinite" : phase === "lights" ? "cr-rev .2s linear infinite" : "none",
                    }} />
                  {/* spinning-wheel blur strips */}
                  {isRunning && (
                    <>
                      <span className="absolute bottom-0 left-[12%] w-5 h-1 rounded-full bg-black/60 blur-[1px]" />
                      <span className="absolute bottom-0 right-[14%] w-5 h-1 rounded-full bg-black/60 blur-[1px]" />
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <div className="absolute bottom-1.5 left-2 right-2 flex justify-between items-end z-10">
          {phase === "lights" && <p className="text-[11px] font-mono text-[#F23645] animate-pulse">engines revving — lights out in a moment…</p>}
          {phase === "racing" && <p className="text-[11px] font-mono text-[#F7B731] animate-pulse">racing — the real contract is settling…</p>}
          {phase === "done" && res && (
            <div className="arc-pop">
              <p className={`text-sm font-black font-mono ${res.won ? "text-[#089981]" : "text-[#F23645]"}`}>
                {res.winner === "red" ? "🔴 RED" : "🔵 BLUE"} TAKES THE FLAG {res.won ? "· YOU WIN 🏆" : "· you lose"}
              </p>
              <p className={`text-[11px] font-mono ${res.won ? "text-[#089981]" : "text-[#F23645]"}`}>
                {res.mode === "risefall"
                  ? <>{res.won
                    ? (res.car === "blue" ? "the tick closed HIGHER (RISE)" : "the tick closed LOWER (FALL)")
                    : (res.car === "blue" ? "the tick didn't close higher (flat or lower)" : "the tick didn't close lower (flat or higher)")}</>
                  : <>last digit {res.digit} ({res.digit % 2 === 0 ? "EVEN" : "ODD"})</>}
                {" · "}{res.won ? `+$${res.profit.toFixed(2)}` : `−$${Math.abs(res.profit).toFixed(2)}`}
              </p>
            </div>
          )}
          {phase === "idle" && !res && <p className="text-[10px] font-mono text-white/60">pick a car · press race</p>}
        </div>
      </div>

      {/* ⚡ signal shortcut — race the strongest setup */}
      {mkt?.signal && !mkt.signal.wait && (
        <button data-testid="carrace-signal-race" disabled={locked}
          onClick={() => { const c = carForSignal(mkt.signal.side); setPicked(c); fire(c); }}
          className="w-full h-12 rounded-lg bg-[#F7B731] hover:brightness-110 text-[#0B0E14] font-black text-sm uppercase tracking-wide transition disabled:opacity-40 active:scale-[0.99]">
          ⚡ Race the signal — {carForSignal(mkt.signal.side) === "red" ? "🔴 RED" : "🔵 BLUE"} · {mkt.signal.side} on {mkt.label || mkt.sym} · {mkt.signal.strength}/100
        </button>
      )}

      {/* 🏁/📈 race-mode toggle — same as Penalty */}
      <div data-testid="carrace-mode-toggle" className="grid grid-cols-2 gap-2">
        <button data-testid="carrace-mode-digits" onClick={() => setCarMode("digits")}
          className={`h-10 rounded-lg border font-mono font-black text-[11px] transition-colors ${!rf ? "border-[#F7B731] bg-[#F7B731]/15 text-[#F7B731]" : "border-white/15 text-white/45 hover:border-white/35"}`}>
          🏁 DIGITS · EVEN / ODD
        </button>
        <button data-testid="carrace-mode-risefall" onClick={() => setCarMode("risefall")}
          className={`h-10 rounded-lg border font-mono font-black text-[11px] transition-colors ${rf ? "border-[#53b9ff] bg-[#53b9ff]/15 text-[#53b9ff]" : "border-white/15 text-white/45 hover:border-white/35"}`}>
          📈 RISE / FALL · red = FALL, blue = RISE
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button data-testid="carrace-pick-red" disabled={locked} onClick={() => setPicked("red")}
          className={`h-16 rounded-lg border transition disabled:opacity-40 active:scale-[0.98] flex flex-col items-center justify-center leading-tight ${picked === "red" ? "border-[#F23645] bg-[#F23645]/15" : "border-white/15 bg-[#131722] hover:border-[#F23645]/60"}`}>
          {rf ? (
            <>
              <span className="font-black text-sm uppercase text-[#ff8a95]">🔴 Red · FALL{mkt && mkt.risePct <= 44 ? " 🔥" : ""}</span>
              <span className="text-[10px] font-mono text-white/60">wins if the next tick closes LOWER{mkt?.risePct != null ? ` · recent ↓ ${100 - mkt.risePct}%` : ""}</span>
            </>
          ) : (
            <>
              <span className="font-black text-sm uppercase text-[#ff8a95]">🔴 Red · EVEN{mkt && mkt.evenPct >= 56 ? " 🔥" : ""}</span>
              <span className="text-[10px] font-mono text-white/60">digits 0·2·4·6·8{mkt?.evenPct != null ? ` · recent E ${mkt.evenPct}%` : ""}</span>
            </>
          )}
        </button>
        <button data-testid="carrace-pick-blue" disabled={locked} onClick={() => setPicked("blue")}
          className={`h-16 rounded-lg border transition disabled:opacity-40 active:scale-[0.98] flex flex-col items-center justify-center leading-tight ${picked === "blue" ? "border-[#2962FF] bg-[#2962FF]/15" : "border-white/15 bg-[#131722] hover:border-[#2962FF]/60"}`}>
          {rf ? (
            <>
              <span className="font-black text-sm uppercase text-[#7fa8ff]">🔵 Blue · RISE{mkt && mkt.risePct >= 56 ? " 🔥" : ""}</span>
              <span className="text-[10px] font-mono text-white/60">wins if the next tick closes HIGHER{mkt?.risePct != null ? ` · recent ↑ ${mkt.risePct}%` : ""}</span>
            </>
          ) : (
            <>
              <span className="font-black text-sm uppercase text-[#7fa8ff]">🔵 Blue · ODD{mkt && mkt.evenPct <= 44 ? " 🔥" : ""}</span>
              <span className="text-[10px] font-mono text-white/60">digits 1·3·5·7·9{mkt?.evenPct != null ? ` · recent O ${100 - mkt.evenPct}%` : ""}</span>
            </>
          )}
        </button>
      </div>
      <button data-testid="carrace-race-btn" disabled={locked || !picked}
        onClick={() => fire(picked)}
        className="w-full h-14 rounded-lg bg-[#F7B731] hover:brightness-110 text-[#0B0E14] font-black text-base uppercase tracking-widest transition disabled:opacity-40 active:scale-[0.99]">
        🏁 RACE{picked ? ` — ${picked === "red" ? "🔴 RED" : "🔵 BLUE"} · ${sideFor(picked, carMode)}` : " — pick a car first"}
      </button>
      <p data-testid="carrace-contract-warning" className="text-center text-[9px] font-mono text-[#ff8a95]">
        ⚠ Each game is a real contract. You can lose your whole stake. Demo uses virtual funds.
      </p>

      <ServerBurstPanel srv={srv} noun="races" />
      <AutoRunBar id="carrace" auto={auto} noun="races" />
      {auto.runs >= 50 && !srv.running && (
        <p className="text-[9px] font-mono text-[#53b9ff]">🖥 ×{auto.runs} runs ON THE VTS SERVER — it keeps racing even if you close the app. Come back anytime for the live score.</p>
      )}

      <MarketScanner key={carMode} id="carrace" mode={rf ? "rise" : "evenodd"} onChange={onScan}
        note={rf ? "↑/↓ = up vs down share of the last 50 ticks · history only, NOT a prediction"
          : "E/O = even vs odd share of the last 50 digits · history only, NOT a prediction"} />

      <p data-testid="carrace-tally" className="text-center text-[11px] font-mono text-white/60">
        Wins <b className="text-[#089981]">{tally.w}</b> · Losses <b className="text-[#F23645]">{tally.l}</b> · Net <b className={tally.net >= 0 ? "text-[#089981]" : "text-[#F23645]"}>${tally.net.toFixed(2)}</b>
      </p>
      <p className="text-center text-[10px] font-mono text-white/40">real Deriv contracts on the locked market · every race is ~50/50 · losses possible</p>
      <style>{`
        @keyframes cr-vibe { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-1.5px) } }
        @keyframes cr-rev { 0%,100% { transform: translateX(0) } 50% { transform: translateX(1.5px) } }
        @keyframes cr-duel-a { 0%,100% { transform: translateX(0) } 30% { transform: translateX(26px) } 60% { transform: translateX(-8px) } 85% { transform: translateX(18px) } }
        @keyframes cr-duel-b { 0%,100% { transform: translateX(8px) } 30% { transform: translateX(-10px) } 60% { transform: translateX(30px) } 85% { transform: translateX(-4px) } }
        .cr-duel-a { animation: cr-duel-a 1.6s ease-in-out infinite; }
        .cr-duel-b { animation: cr-duel-b 1.7s ease-in-out infinite; }
        @keyframes cr-surge { 0% { transform: scale(1) } 40% { transform: scale(1.08) } 100% { transform: scale(1) } }
        .cr-surge { animation: cr-surge .9s ease-out; }
        @keyframes cr-nitro { 0%,100% { opacity: .9; transform: translateY(-50%) scaleX(1) } 50% { opacity: .5; transform: translateY(-50%) scaleX(.6) } }
        .cr-nitro { animation: cr-nitro .14s linear infinite; }
        @keyframes cr-lines { 0% { background-position: 0 0 } 100% { background-position: -520px 0 } }
        .cr-speedlines { animation: cr-lines .5s linear infinite; mask-image: linear-gradient(180deg, transparent 10%, black 35%, black 90%, transparent); }
        @keyframes cr-flag { 0%,100% { transform: rotate(0deg) } 50% { transform: rotate(-14deg) } }
      `}</style>
    </div>
  );
};

export default CarRace;
